import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactGA from "react-ga";
import { getUser } from "examsbook-react-sdk/actions/getUser";
import i18n from "examsbook-react-sdk/translations";
import Navigation from "../Navigation/Navigation";
import HeaderWrapper from "../common/Header/Header";
import { useScreenWidth } from "examsbook-react-sdk/custom-hooks";
import { useSearchQuery } from "../../services/SearchQueryContext";
import ThemeProvider from "../../constants/themeProvider";
import UnderMaintenance from "../common/UnderMaintenance/UnderMaintenance";
import { Box } from "@material-ui/core";
import { BottomNavigationTabs } from "../common/BottomNavigation";
import learnifyFavicon from "../../assets/images/learnifyFavicon.png";
import { getSettingsAction } from "examsbook-react-sdk/actions/getSettingsAction";

function initializeReactGA() {
  ReactGA.initialize("UA-38243486-7");
}

initializeReactGA();

function MainContent({ children }) {
  const [openSidebar, setOpenSidebar] = useState(false);
  const user = useSelector((state) => state.user);
  const userRole = user?.user?.role ? user?.user?.role : user?.role;
  const windowScreenWidth = useScreenWidth();
  const dispatch = useDispatch();
  const screenWidth = 900;
  const { newFlag } = useSearchQuery();
  const [showComponent, setShowComponent] = useState(false);
  const [maintenance, setMaintenance] = useState("");
  const [settingData, setSettingsData] = useState([]);

  let subdomain =
    useSelector((state) => state?.subdomainReducer?.subdomain) ||
    localStorage.getItem("subdomain");

    const scrollablePaths = [
      "/dashboard",
      "/courses",
      "/my-courses",
      "/course-package",
      "/surveys",
      "/support-ticket",
      "/support",
      "/certificate-list",
      "/my-profile"
    ];

    const isScrollable = scrollablePaths.includes(window.location.pathname) || window.location.pathname.startsWith("/reports") || window.location.pathname.startsWith("/course/leaderboard") || /^\/course-package\/[^/]+$/.test(window.location.pathname);

  useEffect(() => {
    if (
      /^\/course\/[^/]+$/.test(window.location.pathname) ||
      /^\/course-package\/[^/]+$/.test(window.location.pathname) ||
      window.location.pathname.includes("/reports") ||
      (windowScreenWidth > 900 && windowScreenWidth < 1024)
    ) {
      setOpenSidebar(false);
    } else {
      setOpenSidebar(windowScreenWidth > screenWidth);
    }
  }, [windowScreenWidth, window.location.pathname]);

  const getSettingsApiData = async () => {
    try {
      await dispatch(getSettingsAction(subdomain)).then((res) => {
        setNewData(res?.data);
        setSettingsData(res?.data);
      });
    } catch (error) {
      console.log("Error -", error.message);
    }
  };

  useEffect(() => {
    if (subdomain && subdomain !== null) {
      getSettingsApiData();
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowComponent(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const maintenanceValue = settingData?.data?.[0]
    ? settingData?.data?.[0]?.customization?.maintenancePage
    : false;
  useEffect(() => {
    if (settingData?.data) {
      setMaintenance(maintenanceValue);
    }
  }, [maintenanceValue]);

  const setNewData = (settingsData) => {
    let language = settingsData?.data[0]?.branding?.language?.defaultLanguage;
    const languageMap = {
      English: "en",
      Hindi: "hn",
      Assamese: "as",
      Bengali: "bn",
      Gujarati: "gu",
      Kannada: "kn",
      Malayalam: "ml",
      Marathi: "mr",
      Odia: "ory",
      Punjabi: "pa",
      Tamil: "ta",
      Telugu: "te",
      Urdu: "ur",
      French: "fr",
      Irish: 'ir',
      German: 'gm',
      Italian: 'it',
      Polish: 'po',
      Swedish: 'sw',
      Romanian: 'ro'
    };
    if (languageMap[language]) {
      language = languageMap[language];
    } else {
      language = "en";
    }

    const storedLanguage = localStorage.getItem("language");
    if (!storedLanguage) {
      localStorage.setItem("language", language);
      i18n.changeLanguage(language);
    } else {
      i18n.changeLanguage(storedLanguage);
    }
    localStorage.setItem("default_language", language);
    let dynamicLogo = "";
    // Setting dynamic favicon
    if (settingsData) {
      const imageValue =
        settingsData?.data[0]?.branding?.logos?.webAndAppFavicon?.imagePath;

      if (imageValue) {
        dynamicLogo = imageValue;
      }
    }

    document.querySelector('link[rel="icon"]').href = dynamicLogo
      ? dynamicLogo
      : learnifyFavicon;
    document.querySelector('link[rel="shortcut icon"]').href = dynamicLogo
      ? dynamicLogo
      : learnifyFavicon;
  };

  //Fetch basic info.
  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  //effect to check for user.
  useEffect(() => {
    if (!user || !user.loggedIn) {
      return;
    }
  }, [user]);

  // Prevent user to open Inspect window from key combinations.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.code === "KeyI") {
        event.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Prevent user to open Inspect window with right click.
  useEffect(() => {
    const handleContextmenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextmenu);
    return function cleanup() {
      document.removeEventListener("contextmenu", handleContextmenu);
    };
  }, []);

  function openDrawer(e) {
    setOpenSidebar(!openSidebar);
    e.preventDefault();
  }

  function closeDrawer() {
    setOpenSidebar(false);
  }
  const path = window.location.pathname;
 
  let conditionalClass = "";
 
  if (
    path === "/login" ||
    path === "/change-password" ||
    path === "/forgot-password"
  ) {
    conditionalClass = "mobile-view-password-spacing";
  } else if (path === "/my-profile") {
    conditionalClass = "mobile-profile-active";
  } else if (
    /^\/quiz\/mock-test\/[^/]+\/[^/]+$/.test(path) ||
    /^\/course\/[^/]+$/.test(path) ||
    /^\/course-package\/[^/]+$/.test(path)
  ) {
    conditionalClass = "course-view-mobile";
  }
  return (
    <Box className="App">
      {showComponent &&
      maintenance &&
      maintenance === true &&
      userRole !== "admin" &&
      userRole !== "superAdmin" ? (
        <Box>
          <UnderMaintenance />
        </Box>
      ) : (
        showComponent && (
          <ThemeProvider>
            <Box
              style={{
                height:
                  newFlag ||
                  /^\/course\/[a-zA-Z0-9]+$/.test(window.location.pathname) ||
                  /^\/course-package\/[a-zA-Z0-9]+$/.test(window.location.pathname)
                    ? "0px"
                    : "70px",
                padding: "0px",
                marginBottom: "0px",
              }}
              className={"container " + (openSidebar ? "open-drawer" : "")}
              onClick={
                windowScreenWidth <= screenWidth
                  ? closeDrawer.bind(this)
                  : () => null
              }
            >
              <HeaderWrapper
                open={openSidebar}
                handleDrawerOpen={openDrawer}
                handleDrawerClose={closeDrawer}
              />
            </Box>
            <Navigation
              openSidebar={openSidebar}
              windowScreenWidth={windowScreenWidth}
              screenWidth={screenWidth}
              closeDrawer={closeDrawer}
            >
              <Box className={`main-div ${conditionalClass}`} style={{ overflowY: isScrollable ? "auto" : "hidden" }}>
                {children}
              </Box>
            </Navigation>
            <BottomNavigationTabs />
          </ThemeProvider>
        )
      )}
    </Box>
  );
}

export default MainContent;
